import React from "react";
import useWindowSize from "../hooks/useWindowSize";

export default function GradientGauge({
  value = 0,
  max = 40,
  size = 220,
  unit = "Nós",
  direction = "",
  fontSizeRatio = 0.1,
  strikeSizeRatio = 15,
}) {
  const stroke = size / strikeSizeRatio;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const progress = Math.min(value / max, 1);
  const dashOffset = circumference * (1 - progress);

  const { width, height } = useWindowSize();
  const styles = buildStyles(width, fontSizeRatio);

  return (
    <div style={styles.wrapper}>
      <div style={{ width: size, height: size, position: "relative" }}>
        <svg width={size} height={size}>
          <defs>
            {/* Gradient rotated to match -90deg stroke start */}
            <linearGradient
              id="windGradient"
              gradientUnits="userSpaceOnUse"
              gradientTransform={`rotate(260 ${size / 2} ${size / 2})`}
              x1="0"
              y1="0"
              x2={size}
              y2="0"
            >
              {/* ✅ START = GREEN */}
              <stop offset="0%" stopColor="#00E5FF" />
              <stop offset="20%" stopColor="#00E676" />

              {/* build up */}
              <stop offset="40%" stopColor="#C6FF00" />
              <stop offset="60%" stopColor="#FFD600" />
              <stop offset="80%" stopColor="#FF9100" />

              {/* END = RED */}
              <stop offset="100%" stopColor="#FF1744" />
            </linearGradient>

            <filter id="glow">
              <feGaussianBlur stdDeviation="4" result="blur" />
              <feMerge>
                <feMergeNode in="blur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>

          {/* Background ring */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke="#E5E7EB"
            strokeWidth={stroke}
          />

          {/* Progress ring */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke="url(#windGradient)"
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={dashOffset}
            filter="url(#glow)"
            style={{
              transition: "stroke-dashoffset 0.9s cubic-bezier(.4,0,.2,1)",
              transform: "rotate(-90deg)",
              transformOrigin: "50% 50%",
            }}
          />
        </svg>

        {/* Center value */}
        <div style={styles.center}>
          <div style={styles.value}>{value}</div>
          <div style={styles.unit}>{unit}</div>
          <div style={styles.direction}>{direction}</div>
        </div>
      </div>
    </div>
  );
}

function buildStyles(size, fontSizeRatio) {
  const VALUE_RATIO = 0.38 * fontSizeRatio; // % of gauge size
  const UNIT_RATIO = 0.12 * fontSizeRatio;
  const DIRECTION_RATIO = 0.12 * fontSizeRatio;

  return {
    wrapper: {
      width: "100%",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      padding: 10,
      boxSizing: "border-box",
    },

    center: {
      position: "absolute",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      textAlign: "center",
      fontFamily: "Montserrat",
    },

    value: {
      fontSize: Math.max(24, size * VALUE_RATIO),
      fontWeight: "bold",
      color: "#111",
      lineHeight: 1,
    },

    unit: {
      fontSize: Math.max(12, size * UNIT_RATIO),
      color: "#555",
      marginTop: 4,
    },

    direction: {
      fontSize: Math.max(12, size * DIRECTION_RATIO),
      color: "#555",
      marginTop: 4,
    },
  };
}
